<script setup lang="ts">
import { api } from '@/api';
import {
  EnableMaintenanceModeRequestDto,
  EnableMaintenanceModeRequestDtoTypeEnum,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import MaintenanceModeDialog from '@/components/maintenanceMode/MaintenanceModeDialog.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useSystemStore } from '@/stores/systemStore.ts';
import { useLoading } from '@/utils/useLoading.ts';
import { useMaintenanceModeDialog } from '@/utils/useMaintenanceModeDialog';
import Sanitize from '@/components/Sanitize/Sanitize.vue';

const systemStore = useSystemStore();

// Maintenance Mode Dialog
const {
  isOpen: isDialogOpen,
  reset,
  openDialog,
  closeDialog,
  exceptionNotification,
  notificationMessage,
  handleExceptionDetails,
  errorHandler,
  loggedInUsers,
} = useMaintenanceModeDialog();

// Form for maintenance mode activation
const { form } = useVeeForm<EnableMaintenanceModeRequestDto>({
  options: {
    initialValues: {
      $type:
        EnableMaintenanceModeRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedSystemComponentDtoEnableMaintenanceModeRequestDtoPtwPtw100AppZeroApplicationShared,
      ignoreLoggedInUsers: false,
    },
  },
  confirmationOptions: {
    skipDirtyCheck: 'always', // it's just the password and confirmation of the logged in users, no need to check here
  },
  mode: 'add',
});

const { handleSubmit, resetForm: resetMaintenanceModeForm } = form;

// Reset function for the component
function resetComponent() {
  closeDialog();
  resetMaintenanceModeForm();
}

// Form submission handler
const submit = handleSubmit(async (enableMaintenanceModeRequestDto) => {
  reset();
  const { success } = await api.systemApi.enableMaintenanceModeInfo(
    {
      enableMaintenanceModeRequestDto,
    },
    [formBackendErrorHandler(form), handleExceptionDetails, errorHandler],
  );
  if (success) {
    await systemStore.fetchMaintenanceModeInfo();
    closeDialog();
  }
});

const { action: toggleMaintenanceMode, loading } = useLoading(async () => {
  if (systemStore.maintenanceModeInfo) {
    await api.systemApi.disableMaintenanceModeInfo();
    // fetch maintenace mode in success and error case (maybe it was already disabled)
    await systemStore.fetchMaintenanceModeInfo();
  } else {
    openDialog();
  }
});
</script>

<template>
  <BasePage>
    <template #title>
      <BasePageTitle :title="$t('MaintenanceMode')" />
    </template>

    <template #actions>
      <PtwCancelButton :to="{ name: routenames.settings }" />
      <Button
        class="ml-2"
        :label="
          $t(
            systemStore.maintenanceModeInfo
              ? 'DeactivateMaintenanceMode'
              : 'ActivateMaintenanceMode',
          )
        "
        variant="primary"
        :loading="loading"
        @click="toggleMaintenanceMode"
      />
    </template>

    <v-sheet class="content pa-4">
      <h4>{{ $t('MaintenanceMode') }}</h4>
      <template v-if="systemStore.maintenanceModeInfo">
        <p>{{ $t('Exceptions.MaintenanceModeIsEnabledMsg') }}</p>
        <p class="mt-2">
          <Sanitize
            :value="
              $t('Exceptions.MaintenanceModeCouldNotDisableMsg', {
                user: `<span class='font-weight-semibold'>${systemStore.maintenanceModeInfo.userName}</span>`,
              })
            "
            :options="{ ALLOWED_TAGS: ['span'] }"
          />
        </p>
      </template>
      <template v-else>
        <p>{{ $t('Exceptions.MaintenanceModeNotEnabledMsg') }}</p>
      </template>
    </v-sheet>

    <!-- Maintenance mode dialog -->
    <MaintenanceModeDialog
      :modelValue="isDialogOpen"
      :loading="loading"
      :exceptionNotification="exceptionNotification"
      :notificationMessage="notificationMessage"
      :loggedInUsers="loggedInUsers"
      :isValid="true"
      @update:modelValue="(value) => !value && resetComponent()"
      @submit="submit"
      @cancel="resetComponent"
    />
  </BasePage>
</template>

<style lang="scss" scoped>
.content {
  min-height: 45vh;
}
</style>
